import { t } from '@lingui/core/macro';
import { UserType } from '@luminovo/api-contracts';
import { GlobalCommandDefinition } from '@luminovo/data-stack';
import { useNavigate } from '@luminovo/design-system';
import { BotMessageSquareIcon, SparklesIcon, UploadIcon } from 'lucide-react';
import React from 'react';

import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';

import { useLumi } from '../LumiPanel';
import { NavigationCommandConfig } from './navigationCommandsConfig';
import { useOpenableCommands } from './useOpenableCommands';

/**
 * Converts a NavigationCommandConfig entry into a GlobalCommandDefinition for the palette.
 */
function toGlobalCommandDefinition(
    config: NavigationCommandConfig,
    navigate: ReturnType<typeof useNavigate>,
): GlobalCommandDefinition {
    return {
        id: config.id,
        label: config.title,
        description: config.description,
        keywords: config.keywords,
        group: config.group,
        icon: React.createElement(config.icon, { size: 16 }),
        onSelect: (ctx) => {
            navigate({ ...config.link, openInNewTab: ctx.openInNewTab });
            ctx.close();
        },
        closeOnComplete: true,
    };
}

/**
 * Hook that returns the navigation commands for the GlobalCommandPalette: those whose page the user may open,
 * for Internal users only.
 *
 * Structure:
 * - Ask Lumi (palette-only, inline): opens the Lumi AI panel; with `onLumiAgent` (the new navigation's), "Lumi Agent",
 *   which calls it
 * - Actions (top-level, from NAVIGATION_COMMANDS_CONFIG): create/add commands
 * - Import... (palette-only grouping parent): children come from config entries
 *   whose id starts with "import-"
 * - Go to (grouped): config entries that have a group field
 *
 * The single source of truth for all navigational commands is NAVIGATION_COMMANDS_CONFIG
 * in navigationCommandsConfig.ts. Add new commands there to have them appear in both
 * the Command Palette and the Quick Actions section on the Home page.
 */
export function useNavigationCommands(onLumiAgent?: () => void): GlobalCommandDefinition[] {
    const navigate = useNavigate();
    const openableCommands = useOpenableCommands();
    const { userType } = useCurrentUserDetailsContext();
    const { openLumi } = useLumi({ shortcut: onLumiAgent === undefined });

    if (userType !== UserType.Internal) {
        return [];
    }

    const commands: GlobalCommandDefinition[] = [];

    // ==========================================================================
    // Ask Lumi — palette-only (no route, UI action)
    // ==========================================================================
    commands.push({
        id: 'ask-lumi',
        label: onLumiAgent ? () => t`Lumi Agent` : () => t`Ask Lumi`,
        icon: React.createElement(onLumiAgent ? BotMessageSquareIcon : SparklesIcon, { size: 20 }),
        keywords: ['ask', 'ai', 'chat'],
        shortcut: ['⌘', 'I'],
        onSelect: (ctx) => {
            (onLumiAgent ?? openLumi)();
            ctx.close();
        },
    });

    // ==========================================================================
    // Top-level actions from shared config (not import-*, not grouped)
    // ==========================================================================
    for (const config of openableCommands) {
        if (!config.id.startsWith('import-') && config.group === undefined) {
            commands.push(toGlobalCommandDefinition(config, navigate));
        }
    }

    // ==========================================================================
    // Import... — palette-only grouping parent; children come from shared config
    // ==========================================================================
    const importChildren: GlobalCommandDefinition[] = openableCommands
        .filter((c) => c.id.startsWith('import-'))
        .map((c) => toGlobalCommandDefinition(c, navigate));

    if (importChildren.length > 0) {
        commands.push({
            id: 'import',
            label: () => t`Import...`,
            description: () => t`Import data from spreadsheet or file`,
            icon: React.createElement(UploadIcon, { size: 20 }),
            keywords: ['import', 'upload', 'csv', 'spreadsheet', 'file'],
            children: importChildren,
        });
    }

    // ==========================================================================
    // "Go to" pages — config entries with a group field
    // ==========================================================================
    for (const config of openableCommands) {
        if (config.group !== undefined) {
            commands.push(toGlobalCommandDefinition(config, navigate));
        }
    }

    return commands;
}
